// Copyright (c) StrangeBee 2024

/*
 *  Helper to put only first letter in uppercase
 */
export const capitalize = (str = ''): string => str.charAt(0).toUpperCase() + str.slice(1);

export const cutString = (str = '', maxSize = 10): string =>
    str.length > maxSize ? str.substring(0, maxSize) + '...' : str;

export const buildPath = (url: string, keyword: string, replaceValue: string): string =>
    url.replace(`:${keyword}`, replaceValue);

export const unBuildPath = (url: string, keyword: string, replaceValue: string): string =>
    url.replace(replaceValue, `:${keyword}`);

export const generateId = (): string => {
    const s4 = (): string => {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    };

    return `${s4()}-${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

export const truncate = (text: string, length = 250): string => {
    if (!text) return '';
    const indexCut = length < text.length ? Math.min(text.lastIndexOf(' ', length), text.length) : text.length;
    const theEndStr = text.length <= indexCut ? '' : ' (...)';
    return text.substring(0, Math.min(indexCut, text.length)) + theEndStr;
};

export const simpleMarkdown = (str: string, addOnRule = []): string => {
    const rules = [
        // link
        ['\\[([^\\[]+)\\]\\(([^\\(]+)\\)', '<a href="$2" target="_blank" rel="noreferrer">$1</a>'],
        // strong
        ['(\\*\\*|__)(.*?)\\1', '<strong>$2</strong>'],

        // Header
        ['(#+) (.*?)\n', '<strong>$2</strong>'],

        // emphasis
        ['(\\*|_)(.*?)\\1', '<em>$2</em>'],
        // strike
        ['(\\~\\~)(.*?)\\1', '<del>$2</del>'],
        // new line
        ['\n', '<br/>'],

        ...addOnRule,
    ];

    return str
        ? rules.reduce((acc, [search, replace]) => {
              return acc.replace(RegExp(search, 'g'), replace);
          }, str)
        : str;
};

/** Removes the last 's' from string, return the string if there is not
 * @warning this does not handle particular cases
 */
export const singularize = (str: string) => (str.endsWith('s') ? str.slice(0, -1) : str);

export const slugify = (str: string): string => {
    const s = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;&';
    const f = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz-------';
    const p = new RegExp(s.split('').join('|'), 'g');

    return str
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(p, (c) => f.charAt(s.indexOf(c)))
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .replace(/^-+|-+$/, '');
};

export const escapeURI = (str: string): string => {
    return encodeURIComponent(str).replace(/[!'()\[\]*]/g, function (c) {
        return '%' + c.charCodeAt(0).toString(16);
    });
};

export const capitalizeFirstLetter = (str = ''): string => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

const htmlUnescapes: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};

/** Used to match HTML entities and HTML characters. */
const reEscapedHtml = /&(?:amp|lt|gt|quot|#(?:0+)?39);/g;
const reHasEscapedHtml = RegExp(reEscapedHtml.source);

export const unescapeHtml = (str = '') => {
    return reHasEscapedHtml.test(str) ? str.replace(reEscapedHtml, (entity) => htmlUnescapes[entity] || "'") : str;
};

// To be used in forms to validate a url
export const urlValidator = (_: any, value: string): Promise<void> => {
    try {
        new URL(value);
        return Promise.resolve();
    } catch (error) {
        if (error instanceof TypeError) return Promise.reject(error.message);
        return Promise.reject();
    }
};

export const replaceAll = (input: string, toReplace: string, byString: string[] | string): string =>
    input.split(toReplace).reduce((str: string, current: string, index: number, base) => {
        if (index < base.length - 1) {
            return str + current + (Array.isArray(byString) ? byString[index] : byString);
        } else {
            return str + current;
        }
    }, '');

export const buildUrlWithQueryParameters = (path: string, parameters: Record<string, string>): string => {
    const entries = Object.entries(parameters);
    if (entries.length == 0) return path;
    const query = entries.map(([key, param]) => `${key}=${encodeURIComponent(param)}`).join('&');
    return `${path}?${query}`;
};
